import { v4 as uuid } from 'uuid';
import { type StateCreator } from 'zustand';
import { AudioLevelMonitor, type AudioStatus } from './AudioLevelMonitor';
import { type AudioGraphStore } from './types';

export interface AudioLevelMonitorSlice {
  audioLevelMonitor: AudioLevelMonitor | null;
  audioStatus: AudioStatus;
  micAudioStatus: AudioStatus;
  audioLevelMonitorUsers: Set<string>;
  initializeAudioLevelMonitor: () => Promise<string>;
  releaseAudioLevelMonitor: (id: string) => void;
}

export const createAudioLevelMonitorSlice: StateCreator<
  AudioGraphStore,
  [],
  [],
  AudioLevelMonitorSlice
> = (set, get) => {
  let initialization: Promise<void> | null = null;

  return {
    audioLevelMonitor: null,
    audioStatus: 'UNKNOWN',
    micAudioStatus: 'UNKNOWN',
    audioLevelMonitorUsers: new Set(),

    initializeAudioLevelMonitor: async () => {
      const id = uuid();
      // Register ownership before awaiting analyzers, so same-tick callers share
      // one initialization and a released caller cannot be re-added later.
      set((state) => ({
        audioLevelMonitorUsers: new Set(state.audioLevelMonitorUsers).add(id),
      }));

      if (!get().audioLevelMonitor) {
        if (!initialization) {
          const attempt = (async () => {
            const { analyzer: micAnalyzer } = await get().getMicAnalyzerTask();
            const { analyzer: tabAnalyzer } = await get().getTabAnalyzerTask();
            if (get().audioLevelMonitorUsers.size === 0) return;

            const audioLevelMonitor = new AudioLevelMonitor(micAnalyzer, tabAnalyzer, {
              peakWindow: 1000,
              onStatusChange: (status: AudioStatus) => set({ audioStatus: status }),
              onMicStatusChange: (status: AudioStatus) => set({ micAudioStatus: status }),
            });
            audioLevelMonitor.start();
            set({ audioLevelMonitor });
          })();
          initialization = attempt;
          void attempt
            .finally(() => {
              if (initialization === attempt) initialization = null;
            })
            .catch(() => undefined);
        }

        try {
          await initialization;
        } catch (error) {
          get().releaseAudioLevelMonitor(id);
          throw error;
        }
      }

      return id;
    },

    releaseAudioLevelMonitor: (id: string) => {
      set((state) => {
        const newUsers = new Set(state.audioLevelMonitorUsers);
        newUsers.delete(id);

        if (newUsers.size === 0 && state.audioLevelMonitor) {
          state.audioLevelMonitor.stop();

          return {
            audioLevelMonitorUsers: newUsers,
            audioLevelMonitor: null,
            audioStatus: 'UNKNOWN',
            micAudioStatus: 'UNKNOWN',
          };
        }

        return { audioLevelMonitorUsers: newUsers };
      });
    },
  };
};
